import React, { useEffect, useMemo, useRef } from "react";
import { Navigate, useLocation, useNavigate, useSearchParams } from "react-router";
import { Spin } from "antd";
import { useAuthTokenLinkCodeClient } from "@companion-core/shared/app/Queries";
import { useAuthStore } from "@companion-core/shared/app/Store/authStore";
import { useBellStore } from "@companion-core/shared/app/Store/bellStore";
import { getHomeRoutes, getModule } from "@companion-core/shared/app/Utils/config";
import { reFetchAllResources } from "@companion-core/shared/app/Utils/apiClient";
import "Assets/Styles/scss/loader.scss";

interface AuthError {
  response?: {
    data?: {
      internal_error_code: number;
    };
  };
}

const LinkCodeClient = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { pathname } = useLocation();
  const linkCode = searchParams.get("link_code") ?? "";
  const authenticated = useAuthStore((state) => state.authenticated);
  const setAuthenticated = useAuthStore((state) => state.setAuthenticated);
  const setIsRestrictedSession = useAuthStore((state) => state.setIsRestrictedSession);
  const setIsPopUpVisible = useBellStore((state) => state.setIsPopUpVisible);

  // Exchange only when there is a code and no session yet.
  const shouldExchange = Boolean(linkCode) && !authenticated;
  const { logged: homeRouteLogged } = getHomeRoutes() || "home";

  const searchWithoutLinkCode = useMemo(() => {
    const next = new URLSearchParams(searchParams);
    next.delete("link_code");
    const search = next.toString();
    return search ? `?${search}` : "";
  }, [searchParams]);

  const { mutate } = useAuthTokenLinkCodeClient({
    onSuccess: () => {
      if (getModule("concierge")?.modules?.bell?.options?.isPopUpVisibleByDefault) {
        setIsPopUpVisible(true);
      }
      setIsRestrictedSession(true);
      setAuthenticated(true);
      reFetchAllResources();
    },
    onError: (err) => {
      setAuthenticated(false);
      if (getModule("sso")?.options?.ssoOnly) {
        navigate({
          pathname: "/sso/error",
          search: `?errorCode=${(err as AuthError)?.response?.data?.internal_error_code}`,
        });
      } else {
        navigate({ pathname: "/login" });
      }
      reFetchAllResources();
    },
  });

  const hasTriggeredExchange = useRef(false);
  useEffect(() => {
    if (!shouldExchange || hasTriggeredExchange.current) return;
    hasTriggeredExchange.current = true;
    mutate({ linkCode });
  }, [shouldExchange, linkCode, mutate]);

  if (authenticated && linkCode) {
    return (
      <Navigate
        to={{
          pathname: pathname?.startsWith("/") ? pathname : `/${homeRouteLogged}`,
          search: searchWithoutLinkCode,
        }}
        replace
      />
    );
  }

  return (
    <div data-testid="link-code" className="loading">
      <Spin data-testid="link-code__loader" size="large" />
    </div>
  );
};

export default LinkCodeClient;
